import type { FieldValues, UseFormReset } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { useRouteMatch } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { pageQueryKey } from "@/react/components/SettingsApp/Site/Pages/hooks/usePage";
import type { Appearance } from "@/react/components/shared/BuilderUI/Store/interfaceSlice";
import { useToast } from "@/react/components/shared/uikit/ToastV2";
import type { ApiError } from "@/react/config/CustomErrors";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import { useCustomThemeConfirmationModal } from "./CustomThemeConfirmationModal";
import type { SiteTheme } from "./constants";
import { DEFAULT_THEMES_QUERY_KEY } from "./useGetThemes";

type ThemeScope = "site" | "page";

interface CreateCustomSiteThemeData {
  appearance: Appearance;
  cssVariables: SiteTheme["cssVariables"];
  name?: string | null;
  setAsDefaultTheme?: boolean;
  themeScope?: ThemeScope;
}

export interface CreateCustomSiteThemeResponse {
  appearance: Appearance;
  css_variables: SiteTheme["cssVariables"];
  custom_colors: SiteTheme["customColors"];
  id: number;
  is_custom: boolean;
  theme_name: string;
}

export const useCreateCustomSiteTheme = ({
  toggleCurrentCanvasTheme,
  reset,
}: {
  toggleCurrentCanvasTheme: (theme: SiteTheme) => void;
  reset: UseFormReset<FieldValues>;
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const match = useRouteMatch<{ pageId: string }>(
    "/settings/pages/edit/:pageId",
  );
  const pageId = Number(match?.params.pageId);

  const { hide: hideCustomThemeConfirmationModal } =
    useCustomThemeConfirmationModal();

  return useMutation<
    CreateCustomSiteThemeResponse,
    ApiError,
    CreateCustomSiteThemeData
  >({
    mutationFn: ({
      name = null,
      cssVariables,
      appearance,
      themeScope,
      setAsDefaultTheme = true,
    }) =>
      reactQueryPost(internalApi.settings.sites.themes.create_custom_theme(), {
        theme_name: name,
        css_variables: cssVariables,
        appearance,
        theme_scope: themeScope,
        page_id: pageId,
        set_as_default_theme: setAsDefaultTheme,
      }),
    onSuccess: data => {
      void queryClient.invalidateQueries(DEFAULT_THEMES_QUERY_KEY);
      void queryClient.invalidateQueries(pageQueryKey(pageId));

      toggleCurrentCanvasTheme({
        name: data.theme_name,
        cssVariables: data.css_variables,
        customColors: data.custom_colors,
        value: data.id,
        isCustom: data.is_custom,
      });

      reset({
        theme: data.css_variables,
      });

      void hideCustomThemeConfirmationModal();

      toast.success(t("changes_saved"));
    },
    onError: error => {
      toast.error(error.message || t("something_went_wrong"));
    },
  });
};
